import classNames from "classnames";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { DashboardLayout } from "components/DashboardLayout";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import Modal from "components/DesignSystem/Modal/Modal";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import { SelectionCell } from "components/DesignSystem/SelectionCell/SelectionCell";
import { SelectionHeaderCell } from "components/DesignSystem/SelectionHeaderCell/SelectionHeaderCell";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import Table from "components/DesignSystem/Table/V2/Table";
import { AddAsset } from "components/FixedAsset/AddAsset";
import { AssetSlider } from "components/FixedAsset/AssetSlider";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import { Cross } from "components/icons/Cross";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { ChangeEvent, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  RowSelectionState,
  useReactTable,
} from "react-table-8.10.7";
import NoFixedAsset from "static/images/NoFixedAsset.svg";
import {
  useDeleteFixedAssetMutation,
  useGetFixedAssetQuery,
  useLazyExportFixedAssetsQuery,
} from "store/apis/fixedAsset";
import { openSlider, setAllFixedAssetIds } from "store/slices/fixedAsset";
import { RootState } from "store/store";
import { FixedAssetResult, Frequency } from "types/Models/fixedAsset";
import { debounce } from "utils/debouncing";
import { pluralize } from "utils/pluralize";
import { EmptyScreen } from "../EmptyScreen";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowDown } from "components/icons/ArrowDown";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { FixedAssetAction } from "./FixedAssetAction";
import { useLazyExportVendorSummaryQuery } from "store/apis/reports";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import ToolTip from "components/design/toolTip";
import { CaretDown } from "components/icons/CaretDown";
import { useAuth } from "hooks/useAuth";
import { DividerLine } from "components/icons/DividerLine";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";

export const RemainingLife = ({
  frequency,
  remainingLife,
}: {
  frequency: Frequency;
  remainingLife: number;
}) => {
  switch (frequency) {
    case "ANNUALLY": {
      return <>{pluralize(remainingLife, "year", "years")}</>;
    }

    case "QUARTERLY": {
      return <>{pluralize(remainingLife, "quarter", "quarters")}</>;
    }

    case "MONTHLY": {
      return <>{pluralize(remainingLife, "month", "months")}</>;
    }

    default:
      return <></>;
  }
};

export const FixedAsset = () => {
  usePageTitle("Fixed Assets");
  const dispatch = useDispatch();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { close, isOpen, open } = useModal();
  const deleteModal = useModal();
  const [searchTerm, setSearchTerm] = useState("");
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const { alertToast, successToast } = useToast();
  const { email } = useAuth();

  const { previewFixedAssetId } = useSelector(
    (store: RootState) => store.fixedAsset
  );

  const [deleteFixedAssets, { isLoading: isDeleting }] =
    useDeleteFixedAssetMutation();

  const [pageNum, setPageNum] = useState(1);

  const [exportFixedAssets, { isFetching: isExporting }] =
    useLazyExportFixedAssetsQuery();

  const { data, isLoading, isSuccess, isFetching } = useGetFixedAssetQuery(
    {
      entityId,
      groupId,
      pageNum,
      searchTerm,
    },
    { skip: !entityId || !groupId }
  );

  const {
    fixed_assets = [],
    per_page = 1,
    total_count = 1,
    total_pages = 1,
  } = data || {};

  const handleSearch = debounce((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchTerm(value);
  });

  const isEmpty = fixed_assets?.length === 0;
  const hasSelection = Object.keys(rowSelection).length > 0;
  const selectedAssetIds = Object.keys(rowSelection);

  const handleDeleteSuccess = (deletedId: string) => {
    setRowSelection((prev) => {
      const updated = { ...prev };
      delete updated[deletedId];
      return updated;
    });
  };

  const createColumn = createColumnHelper<FixedAssetResult>();

  const columns = useMemo(
    () => [
      createColumn.display({
        id: "selection",
        size: 3,
        header: ({ table }) => <SelectionHeaderCell table={table} />,
        cell: ({ row }) => (
          <SelectionCell
            row={row}
            hasSelection={hasSelection}
            visibility="hover"
          />
        ),
      }),

      createColumn.accessor("asset_name", {
        size: 22,
        header: () => <HeadCellContent title="Asset name" />,
        cell: (info) => {
          const name = info.getValue();

          return <>{name}</>;
        },
      }),

      createColumn.accessor("remaining_life", {
        size: 15,
        header: () => <HeadCellContent title="Remaining life" />,
        cell: (info) => {
          const { frequency, remaining_life } = info.row.original;

          return (
            <RemainingLife
              frequency={frequency}
              remainingLife={remaining_life}
            />
          );
        },
      }),

      createColumn.accessor("purchase_price", {
        size: 20,
        header: () => <HeadCellContent title="Purchase price" align="right" />,
        cell: (info) => {
          const purchase_price = info.getValue();

          return (
            <div className="t-flex t-justify-end t-whitespace-nowrap">
              <AmountSuperScript amount={purchase_price} />
            </div>
          );
        },
      }),

      createColumn.accessor("current_value", {
        size: 20,
        header: () => <HeadCellContent title="Current value" align="right" />,
        cell: (info) => {
          const current_value = info.getValue();

          return (
            <div className="t-flex t-justify-end t-whitespace-nowrap">
              <AmountSuperScript amount={current_value} />
            </div>
          );
        },
      }),

      createColumn.accessor("depreciation_amount", {
        size: 15,
        header: () => (
          <HeadCellContent title="Depreciation value" align="right" />
        ),
        cell: (info) => {
          const depreciation_amount = info.getValue();

          return (
            <div className="t-flex t-justify-end t-whitespace-nowrap">
              <AmountSuperScript amount={depreciation_amount} />
            </div>
          );
        },
      }),

      createColumn.accessor("uuid", {
        size: 5,
        header: () => <HeadCellContent title="" />,
        cell: (info) => (
          <FixedAssetAction
            fixedAsset={info.row.original}
            onDeleteSuccess={handleDeleteSuccess}
          />
        ),
      }),
    ],
    [hasSelection, handleDeleteSuccess]
  );

  const table = useReactTable({
    data: fixed_assets || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.uuid,
    enableRowSelection: (row) => row.original.edit_delete_allowed,
    state: {
      rowSelection,
    },
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  const onBulkDelete = async () => {
    try {
      await deleteFixedAssets({
        groupId,
        entityId,
        fixedAssetIds: selectedAssetIds,
      }).unwrap();
      deleteModal.close();
      setRowSelection({});
      successToast({
        message: `${pluralize(
          selectedAssetIds.length,
          "asset",
          "assets"
        )} deleted successfully`,
      });
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  const openRuleSlider = (ruelId: string) => {
    const allFixedAssetIds = fixed_assets.map(({ uuid }) => uuid);

    dispatch(openSlider(ruelId));
    dispatch(setAllFixedAssetIds(allFixedAssetIds));
  };

  const exportSummary = async (exportToMail: boolean = false) => {
    try {
      const result = await exportFixedAssets({
        groupId,
        entityId,
        export_to_mail: exportToMail,
      }).unwrap();

      if (!exportToMail && result.download_url) {
        window.open(result.download_url, "_blank");
      }

      successToast({
        message: exportToMail
          ? `Fixed assets will be sent over mail to ${email} in some time`
          : "Fixed assets downloaded successfully",
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  return (
    <DashboardLayout
      header={
        <Header
          v2
          title="Fixed Assets"
          right={
            <div className="t-flex t-gap-3">
              {!isEmpty && (
                <Dropdown.Root>
                  <Dropdown.Trigger className="t-group" asChild>
                    <div>
                      <ToolTip
                        text={
                          <span className="t-block">
                            Export fixed assets along with their
                            <br />
                            depreciation schedules
                          </span>
                        }
                      >
                        <Button
                          size="xsmall"
                          customType="secondary"
                          disabled={isExporting}
                          isLoading={isExporting}
                        >
                          Export
                          <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out">
                            <ArrowDown
                              strokeWidth={"1.5"}
                              color="currentColor"
                            />
                          </div>
                        </Button>
                      </ToolTip>
                    </div>
                  </Dropdown.Trigger>
                  <Dropdown.Portal>
                    <Dropdown.Content align="end" sideOffset={4}>
                      <ExportOptions
                        onLocalDownload={() => exportSummary(false)}
                        onMailExport={() => exportSummary(true)}
                      />
                    </Dropdown.Content>
                  </Dropdown.Portal>
                </Dropdown.Root>
              )}
              <LockedUi
                feature={WRITE_PERMISSION_KEYS.ACCOUNTING}
                blockedUi={<LockedButton text="Add asset" size="xsmall" />}
              >
                <Button customType="primary" size="xsmall" onClick={open}>
                  Add asset
                </Button>
              </LockedUi>
            </div>
          }
        />
      }
    >
      <DashboardContainer className="t-relative t-gap-5">
        <DashboardContainer.Header className="t-sticky t-top-0 t-z-header t-flex t-flex-col t-gap-3">
          <div className="t-flex t-justify-between">
            <div className="t-w-1/2">
              <Search
                isLoading={isFetching}
                defaultValue={searchTerm}
                block
                placeholder="Search"
                onChange={handleSearch}
              />
            </div>
            <div className="t-flex t-justify-end t-w-full">
              <PaginationHandler
                page_num={pageNum}
                per_page={per_page}
                total_count={total_count}
                total_pages={total_pages}
                onPageNumChange={setPageNum}
                isLoading={isFetching}
              />
            </div>
          </div>
        </DashboardContainer.Header>
        <DashboardContainer.Content>
          <Async.Root {...{ isLoading, isSuccess, isEmpty }}>
            <Async.Empty>
              <EmptyScreen
                text="Automate your fixed asset depreciation"
                cta={
                  <LockedUi
                    feature={WRITE_PERMISSION_KEYS.ACCOUNTING}
                    blockedUi={<LockedButton text="Add asset" />}
                  >
                    <Button customType="primary" size="small" onClick={open}>
                      Add asset
                    </Button>
                  </LockedUi>
                }
              >
                <img src={NoFixedAsset} alt="No fixed asset found" />
              </EmptyScreen>
            </Async.Empty>
            <Async.Success>
              <TableUI table={table} isHeaderCellUpperCase={false}>
                {({ row }) => (
                  <Table.Row
                    key={row.id}
                    onClick={() => openRuleSlider(row.original.uuid)}
                    isRowSelected={row.getIsSelected()}
                    className={classNames(
                      "t-group hover:t-bg-surface-lighter-grey t-cursor-pointer",
                      {
                        "!t-bg-surface-lighter-grey":
                          previewFixedAssetId === row.original.uuid,
                      }
                    )}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <Table.Cell
                          key={cell.id}
                          style={{ width: `${cell.column.getSize()}%` }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                )}
              </TableUI>
              <AssetSlider />
            </Async.Success>
          </Async.Root>
        </DashboardContainer.Content>
        {isOpen && <AddAsset isOpen={isOpen} close={close} />}

        <SelectedFloatingControl>
          {hasSelection && (
            <>
              <span className="t-whitespace-nowrap t-text-subtext t-text-text-60">
                {selectedAssetIds.length} selected
              </span>
              <span className="t-text-neutral-10 t-h-full">
                <DividerLine />
              </span>
              <LockedUi
                feature={WRITE_PERMISSION_KEYS.ACCOUNTING}
                blockedUi={<LockedButton text="Delete" />}
              >
                <Button size="small" onClick={deleteModal.open}>
                  Delete
                </Button>
              </LockedUi>
              <Button
                customType="ghost_icon"
                size="xsmall"
                onClick={() => table.resetRowSelection()}
              >
                <Cross />
              </Button>
            </>
          )}
        </SelectedFloatingControl>

        <Modal.Root open={deleteModal.isOpen} onOpenChange={deleteModal.close}>
          <Modal.Content size="regular">
            <StatusModal
              type="warning"
              title={`Are you sure you want to delete
                ${pluralize(
                  selectedAssetIds.length,
                  "fixed Asset",
                  "fixed Assets"
                )}
                ?`}
            >
              <div className="t-text-body-sm t-text-text-30">
                This action cannot be undone and will permanently delete the
                selected fixed assets and all associated journal entries, which
                may affect other financial reports.
              </div>
            </StatusModal>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild disabled={isDeleting}>
                <Button disabled={isDeleting}>Cancel</Button>
              </Modal.RawClose>
              <Button
                customType="primary"
                onClick={onBulkDelete}
                isLoading={isDeleting}
                disabled={isDeleting}
              >
                Delete
              </Button>
            </Modal.FooterButtonGroup>
          </Modal.Content>
        </Modal.Root>
      </DashboardContainer>
    </DashboardLayout>
  );
};
